import { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Animated,
  Dimensions,
 Platform } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { Award, X, Share2, Dumbbell, Flame, Users, Calendar, Trophy } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import * as Haptics from 'expo-haptics';


interface Badge {
  id: string;
  name: string;
  description: string;
  icon_name: string;
  tier: string;
  category?: string;
}

interface BadgeCelebrationProps {
  badge: Badge | null;
  visible: boolean;
  onClose: () => void;
  onShare?: () => void;
}

interface ConfettiPiece {
  id: number;
  x: Animated.Value;
  y: Animated.Value;
  rotation: Animated.Value;
  color: string;
}

export default function BadgeCelebration({
  badge,
  visible,
  onClose,
  onShare,
}: BadgeCelebrationProps) {
  const { theme } = useTheme();
  const [confetti, setConfetti] = useState<ConfettiPiece[]>([]);
  const scaleAnim = new Animated.Value(0);
  const floatAnim = new Animated.Value(0);

  useEffect(() => {
    if (visible && badge) {
      if (Platform.OS !== 'web') {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      }

      createConfetti();

      Animated.sequence([
        Animated.spring(scaleAnim, {
          toValue: 1,
          tension: 50,
          friction: 7,
          useNativeDriver: true,
        }),
      ]).start();

      Animated.loop(
        Animated.sequence([
          Animated.timing(floatAnim, {
            toValue: -10,
            duration: 2000,
            useNativeDriver: true,
          }),
          Animated.timing(floatAnim, {
            toValue: 0,
            duration: 2000,
            useNativeDriver: true,
          }),
        ])
      ).start();
    }
  }, [visible, badge]);

  const createConfetti = () => {
    const { width, height } = Dimensions.get('window');
    const colors = [
      theme.colors.accent,
      theme.colors.warning,
      '#FFD700',
      '#FF6B6B',
      '#4ECDC4',
      '#95E1D3',
    ];

    const pieces: ConfettiPiece[] = [];
    for (let i = 0; i < 50; i++) {
      const x = new Animated.Value(Math.random() * width);
      const y = new Animated.Value(-50);
      const rotation = new Animated.Value(0);

      pieces.push({
        id: i,
        x,
        y,
        rotation,
        color: colors[Math.floor(Math.random() * colors.length)],
      });

      Animated.parallel([
        Animated.timing(y, {
          toValue: height + 100,
          duration: 3000 + Math.random() * 2000,
          useNativeDriver: true,
        }),
        Animated.timing(rotation, {
          toValue: Math.random() * 720,
          duration: 3000 + Math.random() * 2000,
          useNativeDriver: true,
        }),
      ]).start();
    }

    setConfetti(pieces);
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'fresh_plates':
        return '#CD7F32';
      case 'iron_habit':
        return '#FF6B35';
      case 'built_different':
        return '#FFD700';
      case 'barbell_authority':
        return '#E5E4E2';
      case 'forged_by_iron':
        return '#FF4444';
      case 'duel_standard':
        return '#B9F2FF';
      case 'bronze':
        return '#CD7F32';
      case 'silver':
        return '#C0C0C0';
      case 'gold':
        return '#FFD700';
      case 'platinum':
        return '#E5E4E2';
      case 'diamond':
        return '#B9F2FF';
      default:
        return theme.colors.accent;
    }
  };

  const getTierGradient = (tier: string): [string, string] => {
    switch (tier) {
      case 'fresh_plates':
        return ['#CD7F32', '#8B4513'];
      case 'iron_habit':
        return ['#FF6B35', '#C94C2C'];
      case 'built_different':
        return ['#FFD700', '#FFA500'];
      case 'barbell_authority':
        return ['#E5E4E2', '#B0B0B0'];
      case 'forged_by_iron':
        return ['#FF4444', '#CC0000'];
      case 'duel_standard':
        return ['#B9F2FF', '#4DD0E1'];
      case 'bronze':
        return ['#CD7F32', '#8B4513'];
      case 'silver':
        return ['#E8E8E8', '#999999'];
      case 'gold':
        return ['#FFD700', '#FFA500'];
      case 'platinum':
        return ['#E5E4E2', '#B0B0B0'];
      case 'diamond':
        return ['#B9F2FF', '#4DD0E1'];
      default:
        return [theme.colors.accent, theme.colors.warning];
    }
  };

  const formatTierName = (tier: string): string => {
    const tierMap: Record<string, string> = {
      fresh_plates: 'Fresh Plates',
      iron_habit: 'Iron Habit',
      built_different: 'Built Different',
      barbell_authority: 'Barbell Authority',
      forged_by_iron: 'Forged by Iron',
      duel_standard: 'DUEL Standard',
    };
    return tierMap[tier] || tier.toUpperCase();
  };

  const getBadgeIcon = (category?: string) => {
    switch (category) {
      case 'strength': return Dumbbell;
      case 'consistency': return Flame;
      case 'social': return Users;
      case 'milestone': case 'milestones': return Calendar;
      case 'status': return Trophy;
      default: return Award;
    }
  };

  if (!badge) return null;

  const BadgeIcon = getBadgeIcon(badge.category);

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={styles.overlay}>
        {confetti.map(piece => (
          <Animated.View
            key={piece.id}
            style={[
              styles.confettiPiece,
              {
                backgroundColor: piece.color,
                transform: [
                  { translateX: piece.x },
                  { translateY: piece.y },
                  {
                    rotate: piece.rotation.interpolate({
                      inputRange: [0, 360],
                      outputRange: ['0deg', '360deg'],
                    }),
                  },
                ],
              },
            ]}
          />
        ))}

        <View style={[styles.content, { backgroundColor: theme.colors.surface }]}>
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <X size={24} color={theme.colors.textSecondary} />
          </TouchableOpacity>

          <Text style={[styles.title, { color: theme.colors.text }]}>Badge Unlocked</Text>

          <Animated.View
            style={[
              styles.badgeContainer,
              {
                transform: [{ scale: scaleAnim }, { translateY: floatAnim }],
              },
            ]}>
            <LinearGradient
              colors={getTierGradient(badge.tier)}
              style={styles.badgeCircle}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}>
              <View style={styles.badgeInner}>
                <BadgeIcon size={64} color={theme.colors.text} />
              </View>
            </LinearGradient>
          </Animated.View>

          <Text
            style={[
              styles.badgeName,
              { color: getTierColor(badge.tier), textShadowColor: getTierColor(badge.tier) + '40' },
            ]}>
            {badge.name}
          </Text>

          <View style={[styles.tierBadge, { backgroundColor: getTierColor(badge.tier) + '20' }]}>
            <Text style={[styles.tierText, { color: getTierColor(badge.tier) }]}>
              {formatTierName(badge.tier).toUpperCase()}
            </Text>
          </View>

          <Text style={[styles.description, { color: theme.colors.textSecondary }]}>
            {badge.description}
          </Text>

          <View style={styles.actions}>
            {onShare && (
              <TouchableOpacity
                style={[
                  styles.shareButton,
                  { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.borderLight },
                ]}
                onPress={onShare}>
                <Share2 size={20} color={theme.colors.text} />
                <Text style={[styles.shareButtonText, { color: theme.colors.text }]}>
                  Share
                </Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.continueButton, { backgroundColor: theme.colors.accent }]}
              onPress={onClose}>
              <Text style={[styles.continueButtonText, { color: theme.colors.accentForeground }]}>
                Continue
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  confettiPiece: {
    position: 'absolute',
    width: 10,
    height: 10,
    borderRadius: 2,
  },
  content: {
    borderRadius: 2,
    padding: 32,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    padding: 8,
    zIndex: 1,
  },
  title: {
    fontSize: 28,
    fontWeight: '900',
    marginBottom: 32,
    textAlign: 'center',
  },
  badgeContainer: {
    marginBottom: 24,
  },
  badgeCircle: {
    width: 160,
    height: 160,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    elevation: 8,
  },
  badgeInner: {
    width: 140,
    height: 140,
    borderRadius: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeName: {
    fontSize: 24,
    fontWeight: '900',
    marginBottom: 12,
    textAlign: 'center',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 8,
  },
  tierBadge: {
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 2,
    marginBottom: 16,
  },
  tierText: {
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 1.5,
  },
  description: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 32,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
  shareButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 0,
    borderWidth: 2,
  },
  shareButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
  continueButton: {
    flex: 1,
    paddingVertical: 16,
    borderRadius: 0,
    alignItems: 'center',
  },
  continueButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
});
